import { Stack, Typography, InputAdornment, IconButton, Chip, Button } from '@mui/material';
import { TableCustom, PrimaryTableHeader } from 'app/components/elements/TableCustom';
import { TextEllipsis } from 'app/components/elements/TextEllipsis';
import { formatAppDate, formatAppDateTime } from 'utils/dateFormat';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AdHocIssue, getAdHocIssues, updateAdHocIssue } from 'services/report';
import { usePaging } from 'hooks/usePaging';
import { SIZE_VALIDATION_ASSET_MODEL } from 'const/paging';
import { InputText } from 'app/components/elements/InputText';
import { InputSelect } from 'app/components/elements/InputSelect';
import { SelectChangeEvent } from '@mui/material/Select';
import { useSearchParams } from 'react-router-dom';
import { AdHocIssueDialog } from './AdHocIssueDialog';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { getIssuesConfig } from 'services/option';
import {
  DEFAULT_ISSUES_UI_CONFIG,
  getAdHocDueDateColor,
  IssuesUiConfig
} from 'utils/issueDueDateColor';
import { CriticalityChip } from './CriticalityChip';
import {
  getCriticalityDisplay,
  DEFAULT_CRITICALITY_LEVELS,
  toCriticalitySelectOptions
} from 'utils/issueCriticality';
import { getUsers } from 'services/option';
import { exportAdHocIssuesCsv } from '../utils/issuesListExport';
import {
  IssuesFiltersBar,
  IssuesFilterField,
  IssuesSearchField,
  filterFieldSx
} from './IssuesFiltersBar';
import { IssuesTabHeader } from './IssuesTabHeader';

const SORT_DUE_DATE_PRIORITY = '__dueDatePriority__';

const AD_HOC_HEADERS: PrimaryTableHeader[] = [
  { label: 'Client Name', key: 'clientName', width: 150, sort: 'client.clientName' },
  { label: 'Main Location', key: 'locationL1', width: 160, sort: 'locationPathString' },
  { label: 'Sub 1', key: 'locationL2', width: 160, sort: 'locationPathString' },
  { label: 'Sub 2', key: 'locationL3', width: 160, sort: 'locationPathString' },
  { label: 'Vertical', key: 'locationL4', width: 160, sort: 'locationPathString' },
  { label: 'Room', key: 'locationL5', width: 160, sort: 'locationPathString' },
  { label: 'Issue Name', key: 'name', width: 180, sort: 'name' },
  { label: 'Issue Description', key: 'description', width: 300 },
  { label: 'Reported By', key: 'reportedBy', width: 160, sort: 'reportedByUser.name' },
  { label: 'Reported At', key: 'reportedAt', width: 150, sort: 'createdAt' },
  { label: 'Assigned By', key: 'assignedBy', width: 140 },
  { label: 'Assigned To', key: 'assignedTo', width: 140 },
  { label: 'Due Date', key: 'dueDate', width: 150, sort: SORT_DUE_DATE_PRIORITY },
  { label: 'Criticality', key: 'criticality', width: 120 },
  { label: 'Status', key: 'status', width: 160, sort: 'status' },
  { label: 'Actions', key: 'actions', width: 220 }
];

export type AdHocApiFilters = {
  clientId?: number;
  locationId?: number;
  reportedByUserId?: number;
  assignedToUserId?: number;
  criticalityKey?: string;
  status?: string;
  keyword?: string;
};

type AdHocIssuesTableProps = {
  view: 'active' | 'archived';
  initialIssueId?: number;
};

const STATUS_OPTIONS_ACTIVE = [
  { label: 'All statuses', value: '' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Resolved', value: 'RESOLVED' }
];

const STATUS_OPTIONS_ARCHIVED = [
  { label: 'All statuses', value: '' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Resolved', value: 'RESOLVED' },
  { label: 'Archived', value: 'ARCHIVED' }
];

export const AdHocIssuesTable = ({ view, initialIssueId }: AdHocIssuesTableProps) => {
  const isArchivedView = view === 'archived';
  const [issues, setIssues] = useState<AdHocIssue[]>([]);
  const [apiFilters, setApiFilters] = useState<AdHocApiFilters>({});
  const [issueStack, setIssueStack] = useState<number[]>(
    initialIssueId != null ? [initialIssueId] : []
  );
  const [search, setSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const [issuesConfig, setIssuesConfig] = useState<IssuesUiConfig>(DEFAULT_ISSUES_UI_CONFIG);
  const [sort, setSort] = useState<{ field?: string; order?: string }>({
    field: SORT_DUE_DATE_PRIORITY,
    order: 'asc'
  });
  const [userOptions, setUserOptions] = useState<{ label: string; value: string }[]>([]);
  const [exporting, setExporting] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const { pagingInfo, setCurrentPage, setTotal } = usePaging({
    rowsPerPage: SIZE_VALIDATION_ASSET_MODEL
  });

  useEffect(() => {
    if (initialIssueId) {
      setIssueStack([initialIssueId]);
    }
  }, [initialIssueId]);

  useEffect(() => {
    let cancelled = false;
    getIssuesConfig()
      .then((res) => {
        if (cancelled) return;
        setIssuesConfig(res?.config || DEFAULT_ISSUES_UI_CONFIG);
      })
      .catch(() => {
        if (!cancelled) setIssuesConfig(DEFAULT_ISSUES_UI_CONFIG);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    getUsers({ page: 1, size: 99999 })
      .then((res) => {
        const list = (res?.users ?? res?.data ?? []) as { id: number; name?: string }[];
        setUserOptions(
          list
            .map((u) => ({ label: u.name || `User ${u.id}`, value: String(u.id) }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
        );
      })
      .catch(() => setUserOptions([]));
  }, []);

  const fetchIssues = useCallback(
    async (
      params: { size: number; page: number; orderBy?: string; order?: string } & AdHocApiFilters
    ) => {
      const data = await getAdHocIssues({
        ...params,
        archived: isArchivedView
      });
      setIssues(data.issues);
      setTotal(data.total);
      return data;
    },
    [isArchivedView, setTotal]
  );

  const { asyncCallback: asyncFetchIssues } = useAsyncCallback(fetchIssues, [fetchIssues]);

  useEffect(() => {
    asyncFetchIssues({
      size: SIZE_VALIDATION_ASSET_MODEL,
      page: pagingInfo.currentPage,
      orderBy: sort.field,
      order: sort.order,
      ...apiFilters
    });
  }, [asyncFetchIssues, pagingInfo.currentPage, apiFilters, sort.field, sort.order]);

  const refreshList = useCallback(async () => {
    await asyncFetchIssues({
      size: SIZE_VALIDATION_ASSET_MODEL,
      page: pagingInfo.currentPage,
      orderBy: sort.field,
      order: sort.order,
      ...apiFilters
    });
  }, [asyncFetchIssues, pagingInfo.currentPage, apiFilters, sort.field, sort.order]);

  const onSortHeader = useCallback(
    (sortField: string) => {
      setSort((prev) => {
        const isSame = prev.field === sortField;
        const nextOrder =
          sortField === SORT_DUE_DATE_PRIORITY
            ? 'asc'
            : isSame
              ? prev.order === 'asc'
                ? 'desc'
                : 'asc'
              : 'asc';
        return { field: sortField, order: nextOrder };
      });
      setCurrentPage(1);
    },
    [setCurrentPage]
  );

  const splitLocationPath = useCallback((path: string | undefined) => {
    const raw = String(path || '').trim();
    if (!raw) return ['', '', '', '', ''] as const;
    const parts = raw
      .split('_')
      .map((p) => p.trim())
      .filter(Boolean);
    return [
      parts[0] || '',
      parts[1] || '',
      parts[2] || '',
      parts[3] || '',
      parts.slice(4).join('_') || ''
    ] as const;
  }, []);

  const toggleFilter = useCallback(
    (key: keyof AdHocApiFilters, value: number | string | undefined) => {
      setApiFilters((prev) => {
        const current = prev[key];
        const next = current === value ? undefined : (value as AdHocApiFilters[typeof key]);
        if (next === undefined) {
          const nextFilters = { ...prev };
          delete nextFilters[key];
          return nextFilters;
        }
        return { ...prev, [key]: next };
      });
      setCurrentPage(1);
    },
    [setCurrentPage]
  );

  const onSearch = useCallback(() => {
    const trimmed = search.trim();
    setSearchText(trimmed);
    setApiFilters((prev) => {
      if (!trimmed) {
        const next = { ...prev };
        delete next.keyword;
        return next;
      }
      return { ...prev, keyword: trimmed };
    });
    setCurrentPage(1);
  }, [search, setCurrentPage]);

  const rows = useMemo(() => {
    return issues.map((issue) => {
      const clientId = issue.client?.id ?? issue.clientId;
      const locationLabel = issue.locationPathString || issue?.locationPathString || '';
      const [l1, l2, l3, l4, l5] = splitLocationPath(locationLabel);
      const locationL1Id = issue.locationLevelIds?.l1Id;
      const locationL2Id = issue.locationLevelIds?.l2Id;
      const locationL3Id = issue.locationLevelIds?.l3Id;
      const locationL4Id = issue.locationLevelIds?.l4Id;
      const locationL5Id = issue.locationLevelIds?.l5Id;
      const reportedByUserId = issue.reportedByUser?.id;
      const isPotentialDuplicate = !!issue.isPotentialDuplicate || !!issue.possibleDuplicateOfId;
      const showArchiveAction = !isArchivedView && issue.status !== 'ARCHIVED';

      return {
        clientName: {
          value: (
            <TextEllipsis
              text={issue.client?.clientName || ''}
              width="95%"
              active={apiFilters.clientId === clientId}
              onClick={() => {
                if (clientId == null) return;
                toggleFilter('clientId', clientId);
              }}
            />
          )
        },
        locationL1: {
          value: (
            <TextEllipsis
              text={l1 || '-'}
              width="95%"
              active={!!l1 && locationL1Id != null && apiFilters.locationId === locationL1Id}
              onClick={() => {
                if (!l1 || locationL1Id == null) return;
                toggleFilter('locationId', locationL1Id);
              }}
            />
          )
        },
        locationL2: {
          value: (
            <TextEllipsis
              text={l2 || '-'}
              width="95%"
              active={!!l2 && locationL2Id != null && apiFilters.locationId === locationL2Id}
              onClick={() => {
                if (!l2 || locationL2Id == null) return;
                toggleFilter('locationId', locationL2Id);
              }}
            />
          )
        },
        locationL3: {
          value: (
            <TextEllipsis
              text={l3 || '-'}
              width="95%"
              active={!!l3 && locationL3Id != null && apiFilters.locationId === locationL3Id}
              onClick={() => {
                if (!l3 || locationL3Id == null) return;
                toggleFilter('locationId', locationL3Id);
              }}
            />
          )
        },
        locationL4: {
          value: (
            <TextEllipsis
              text={l4 || '-'}
              width="95%"
              active={!!l4 && locationL4Id != null && apiFilters.locationId === locationL4Id}
              onClick={() => {
                if (!l4 || locationL4Id == null) return;
                toggleFilter('locationId', locationL4Id);
              }}
            />
          )
        },
        locationL5: {
          value: (
            <TextEllipsis
              text={l5 || '-'}
              width="95%"
              active={!!l5 && locationL5Id != null && apiFilters.locationId === locationL5Id}
              onClick={() => {
                if (!l5 || locationL5Id == null) return;
                toggleFilter('locationId', locationL5Id);
              }}
            />
          )
        },
        name: {
          value: <TextEllipsis text={issue.name || `Adhoc Issue #${issue.id}`} width="95%" />
        },
        description: {
          value: (
            <Typography whiteSpace="pre-line" width="95%">
              {issue.description}
            </Typography>
          )
        },
        reportedBy: {
          value: (
            <TextEllipsis
              text={issue.reportedByUser?.name || ''}
              width="95%"
              active={apiFilters.reportedByUserId === reportedByUserId}
              onClick={() => {
                if (reportedByUserId == null) return;
                toggleFilter('reportedByUserId', reportedByUserId);
              }}
            />
          )
        },
        reportedAt: {
          value: <TextEllipsis text={formatAppDateTime(issue.createdAt)} width="95%" />
        },
        assignedBy: {
          value: <TextEllipsis text={issue.assignedByUser?.name || '-'} width="95%" />
        },
        assignedTo: {
          value: (
            <TextEllipsis
              text={issue.assignedToUser?.name || '-'}
              width="95%"
              active={
                issue.assignedToUser?.id != null &&
                apiFilters.assignedToUserId === issue.assignedToUser.id
              }
              onClick={() => {
                if (issue.assignedToUser?.id == null) return;
                toggleFilter('assignedToUserId', issue.assignedToUser.id);
              }}
            />
          )
        },
        dueDate: {
          value: issue.dueDate ? (
            <Typography
              width="95%"
              color={getAdHocDueDateColor(issue.dueDate, issue.status, issuesConfig)}>
              {formatAppDate(issue.dueDate)}
            </Typography>
          ) : (
            <Typography width="95%" fontSize={12} color="text.secondary">
              -
            </Typography>
          )
        },
        criticality: {
          value: (
            <CriticalityChip
              criticality={
                issue.criticality ||
                getCriticalityDisplay(
                  issue.criticalityKey,
                  issuesConfig.criticalityLevels?.length
                    ? issuesConfig.criticalityLevels
                    : DEFAULT_CRITICALITY_LEVELS
                )
              }
            />
          )
        },
        status: {
          value: (
            <Stack direction="row" alignItems="center" gap={0.5} width="95%">
              <Chip
                size="small"
                label={issue.status}
                sx={{ fontSize: 11, height: 22 }}
                color={
                  issue.status === 'ARCHIVED'
                    ? 'default'
                    : issue.status === 'RESOLVED'
                      ? 'success'
                      : 'primary'
                }
                onClick={() => {
                  if (!issue.status) return;
                  toggleFilter('status', issue.status);
                }}
              />
              {isPotentialDuplicate && (
                <Chip
                  size="small"
                  label="Duplicate?"
                  sx={{ fontSize: 10, height: 20 }}
                  color="warning"
                />
              )}
            </Stack>
          )
        },
        actions: {
          value: (
            <Stack direction="column" gap={1} alignItems="flex-start">
              <div style={{ padding: '0px 5px' }}>
                <Typography
                  fontSize={12}
                  color="primary"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setIssueStack([issue.id])}>
                  View
                </Typography>
              </div>

              {isPotentialDuplicate && (
                <>
                  {showArchiveAction && (
                    <Button
                      size="small"
                      variant="text"
                      sx={{ fontSize: 11, minWidth: 0, paddingX: 0.5 }}
                      onClick={async () => {
                        await updateAdHocIssue(issue.id, { status: 'ARCHIVED' });
                        await refreshList();
                      }}>
                      Archive
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="text"
                    sx={{ fontSize: 11, minWidth: 0, paddingX: 0.5 }}
                    onClick={async () => {
                      await updateAdHocIssue(issue.id, { possibleDuplicateOfId: null });
                      await refreshList();
                    }}>
                    Mark Unique
                  </Button>
                </>
              )}
            </Stack>
          )
        }
      };
    });
  }, [
    apiFilters,
    isArchivedView,
    issues,
    issuesConfig,
    refreshList,
    splitLocationPath,
    toggleFilter
  ]);

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
  };

  const criticalityLevels = issuesConfig.criticalityLevels?.length
    ? issuesConfig.criticalityLevels
    : DEFAULT_CRITICALITY_LEVELS;

  const criticalityOptions = useMemo(
    () => [
      { label: 'All criticalities', value: '' },
      ...toCriticalitySelectOptions(criticalityLevels)
    ],
    [criticalityLevels]
  );

  const clearFilters = useCallback(() => {
    setApiFilters({});
    setSearch('');
    setSearchText('');
    setCurrentPage(1);
  }, [setCurrentPage]);

  const handleExportCsv = useCallback(async () => {
    setExporting(true);
    try {
      await exportAdHocIssuesCsv(
        {
          orderBy: sort.field,
          order: sort.order,
          ...apiFilters,
          archived: isArchivedView
        },
        criticalityLevels
      );
    } finally {
      setExporting(false);
    }
  }, [apiFilters, criticalityLevels, isArchivedView, sort.field, sort.order]);

  const hasActiveFilters = Object.keys(apiFilters).length > 0 || searchText.trim().length > 0;

  const title = isArchivedView ? 'Archived Ad Hoc Issues' : 'Ad Hoc Issues';
  const description = isArchivedView
    ? 'Archived ad hoc issues, including those archived when a client was archived. Open an issue to change its status and move it back to the active list.'
    : 'Ad Hoc issues are quick, field-reported items that are not part of a formal inspection.';

  return (
    <Stack gap={2.5} width="100%">
      <IssuesTabHeader title={title} description={description} />

      <IssuesFiltersBar
        exporting={exporting}
        onExportCsv={handleExportCsv}
        showClearFilters={hasActiveFilters}
        onClearFilters={clearFilters}
        helperText="Search or use filters below. You can also click client, location, status, or reporter in the table to refine results.">
        <IssuesSearchField onSearch={onSearch}>
          <InputText
            title="Search"
            placeholder="Name, location or description…"
            startAdornment={
              <InputAdornment position="end">
                <IconButton size="small" onClick={onSearch}>
                  <img alt="icon-search" src="/icons/icon_search.svg" width={20} />
                </IconButton>
              </InputAdornment>
            }
            inputProps={{
              value: search,
              onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                setSearch(event.target.value),
              onKeyDown: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                if (event.key === 'Enter') onSearch();
              },
              style: { minHeight: '46px', height: '46px', padding: 0 }
            }}
          />
        </IssuesSearchField>

        <IssuesFilterField xs={6} sm={4} md={3} lg={3}>
          <InputSelect
            title="Status"
            value={apiFilters.status || ''}
            placeholder="All statuses"
            sx={filterFieldSx}
            options={isArchivedView ? STATUS_OPTIONS_ARCHIVED : STATUS_OPTIONS_ACTIVE}
            onChange={(e: SelectChangeEvent<unknown>) => {
              const val = e.target.value as string;
              toggleFilter('status', val || undefined);
            }}
          />
        </IssuesFilterField>

        <IssuesFilterField xs={6} sm={4} md={3} lg={3}>
          <InputSelect
            title="Criticality"
            value={apiFilters.criticalityKey || ''}
            placeholder="All criticalities"
            sx={filterFieldSx}
            options={criticalityOptions}
            onChange={(e: SelectChangeEvent<unknown>) => {
              const val = e.target.value as string;
              toggleFilter('criticalityKey', val || undefined);
            }}
          />
        </IssuesFilterField>

        <IssuesFilterField xs={12} sm={4} md={4} lg={3}>
          <InputSelect
            title="Assigned To"
            value={apiFilters.assignedToUserId != null ? String(apiFilters.assignedToUserId) : ''}
            placeholder="All assignees"
            sx={filterFieldSx}
            options={[{ label: 'All', value: '' }, ...userOptions]}
            onChange={(e: SelectChangeEvent<unknown>) => {
              const val = e.target.value as string;
              toggleFilter('assignedToUserId', val ? Number(val) : undefined);
            }}
          />
        </IssuesFilterField>
      </IssuesFiltersBar>

      <TableCustom
        isScrollX
        data={rows}
        headers={AD_HOC_HEADERS}
        onSortHeader={onSortHeader}
        sort={sort}
        handleChooseRow={() => {}}
        pagingInfo={pagingInfo}
        handleChangePage={handleChangePage}
      />

      <AdHocIssueDialog
        mode="edit"
        isOpen={issueStack.length > 0}
        issueId={issueStack[issueStack.length - 1]}
        canGoBack={issueStack.length > 1}
        onBack={() => setIssueStack((prev) => prev.slice(0, -1))}
        onClose={() => {
          setIssueStack([]);
          if (searchParams.get('adHocIssueId')) {
            const next = new URLSearchParams(searchParams.toString());
            next.delete('adHocIssueId');
            setSearchParams(next);
          }
        }}
        onSaved={async () => {
          await refreshList();
        }}
        onOpenIssue={(id) => {
          setIssueStack((prev) => [...prev, id]);
        }}
      />
    </Stack>
  );
};
